import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Select from "react-select";
import toast from "react-hot-toast";
import { FiDownload, FiPlus, FiX } from "react-icons/fi";
import { formatNaira } from "../../../utils/dashboardFormat";
import { useGetContributionCategoriesQuery, useGetMembersContributionHistoryV2Query } from "../../../store/admin/contribution";
import { useGetAllMembersQuery, useGetPaymentMethodsQuery } from "../../../store/selectableSlice";
import { activeFilters, dateRange, listFrom, normalizePayments } from "../../../components/Contributions/contribModel";
import { downloadContributionReport } from "../../../components/Contributions/exportReport";
import { useDebounced } from "../../../components/Contributions/hooks";
import PaymentsTable from "../../../components/Contributions/PaymentsTable";
import { Notice, PageHeader, Pager, SearchBox, Skeleton } from "../../../components/Contributions/parts";

const RANGES = [
  { key: "all", label: "Any date" },
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "custom", label: "Custom dates" },
];

const selectStyles = {
  control: (base) => ({ ...base, minHeight: 40, borderRadius: 10, borderColor: "#e4e1e1", boxShadow: "none" }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

/**
 * Every contribution payment, with filters that stay visible, a total for what you are looking at,
 * and rows that open to show everything about a payment.
 */
export default function AllPayments() {
  const [params] = useSearchParams();
  const [search, setSearch] = useState("");
  const [member, setMember] = useState(params.get("member") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [method, setMethod] = useState("");
  const [range, setRange] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const q = useDebounced(search, 450);

  const quick = range === "custom" ? { from, to } : dateRange(range);
  useEffect(() => setPage(1), [q, member, category, method, range, from, to]);

  const members = listFrom(useGetAllMembersQuery().data);
  const categories = listFrom(useGetContributionCategoriesQuery().data, "categories");
  const methods = listFrom(useGetPaymentMethodsQuery().data, "payment_methods", "paymentMethods");

  const memberOptions = useMemo(
    () => [
      { value: "", label: "All members" },
      ...members.map((m) => ({
        value: String(m.id ?? m.member_id ?? ""),
        label: [m.first_name, m.last_name].filter(Boolean).join(" ") || m.name || "Member",
        no: m.member_no,
      })),
    ],
    [members]
  );

  const filters = {
    member_id: member || undefined,
    contribution_category_id: category || undefined,
    payment_type_id: method || undefined,
    from_date: quick.from || undefined,
    to_date: quick.to || undefined,
    search: q || undefined,
  };
  const { data, isLoading, isFetching, isError, refetch } = useGetMembersContributionHistoryV2Query({ page, limit: 20, ...filters });
  const result = useMemo(() => normalizePayments(data), [data]);

  const names = {
    member: memberOptions.find((o) => o.value === member)?.label,
    category: categories.find((c) => String(c.id) === String(category))?.name,
    method: methods.find((m) => String(m.id ?? m.value) === String(method))?.name,
  };
  const active = activeFilters({ member, category, method, range, search: q }, names);

  const clear = (key) => {
    if (key === "search") setSearch("");
    if (key === "member") setMember("");
    if (key === "category") setCategory("");
    if (key === "method") setMethod("");
    if (key === "range") {
      setRange("all");
      setFrom("");
      setTo("");
    }
  };
  const clearAll = () => ["search", "member", "category", "method", "range"].forEach(clear);

  const [exporting, setExporting] = useState(false);
  const exportAs = async (format) => {
    setExporting(true);
    try {
      await downloadContributionReport(format, filters);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setExporting(false);
    }
  };

  const busy = isLoading || isFetching;

  return (
    <section className="cb" aria-label="All payments">
      <PageHeader
        title="All payments"
        sub="Every contribution payment, with filters for member, category, method and date"
        actions={
          <>
            <button type="button" className="cb-btn" disabled={exporting} onClick={() => exportAs("excel")}>
              <FiDownload size={16} aria-hidden="true" /> {exporting ? "Preparing..." : "Export CSV"}
            </button>
            <button type="button" className="cb-btn" disabled={exporting} onClick={() => exportAs("pdf")}>
              Printable
            </button>
            <Link to="/super_admin/contribution/record_contribution" className="cb-btn primary">
              <FiPlus size={16} aria-hidden="true" /> Record a payment
            </Link>
          </>
        }
      />

      <div className="cb-card">
        <div className="cb-toolbar" role="search" aria-label="Filter payments">
          <SearchBox value={search} onChange={setSearch} placeholder="Search by reference, category or amount" label="Search payments" />
          <div style={{ minWidth: 200, flex: "1 1 200px" }}>
            <Select
              aria-label="Member"
              options={memberOptions}
              value={memberOptions.find((o) => o.value === member) || memberOptions[0]}
              onChange={(o) => setMember(o?.value || "")}
              isSearchable
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              styles={selectStyles}
              formatOptionLabel={(o) => (
                <span>
                  {o.label}
                  {o.no ? <span style={{ color: "#8a8a90", fontSize: 12 }}> #{o.no}</span> : null}
                </span>
              )}
            />
          </div>
          <select className="cb-field" aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select className="cb-field" aria-label="Payment method" value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="">Any method</option>
            {methods.map((m) => (
              <option key={m.id ?? m.value} value={m.id ?? m.value}>{m.name || m.label}</option>
            ))}
          </select>
          <select className="cb-field" aria-label="Date range" value={range} onChange={(e) => setRange(e.target.value)}>
            {RANGES.map((r) => (
              <option key={r.key} value={r.key}>{r.label}</option>
            ))}
          </select>
          {range === "custom" ? (
            <>
              <input className="cb-field" type="date" aria-label="From date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
              <input className="cb-field" type="date" aria-label="To date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </>
          ) : null}
        </div>

        {active.length ? (
          <div className="cb-chips" style={{ marginBottom: 12 }} aria-label="Active filters">
            {active.map((f) => (
              <button key={f.key} type="button" className="cb-chip on" onClick={() => clear(f.key)} aria-label={`Remove filter ${f.label}`}>
                {f.label} <FiX size={12} aria-hidden="true" style={{ verticalAlign: -1 }} />
              </button>
            ))}
            <button type="button" className="cb-chip" onClick={clearAll}>Clear all</button>
          </div>
        ) : null}

        {result.summary.known ? (
          <p className="cb-mute" style={{ marginBottom: 10 }} aria-live="polite">
            <strong style={{ color: "var(--cb-ink)" }}>{result.summary.count}</strong> {result.summary.count === 1 ? "payment" : "payments"},{" "}
            <strong style={{ color: "var(--cb-ink)" }}>{formatNaira(result.summary.total)}</strong> received
            {result.summary.pending ? `, ${result.summary.pending} waiting for confirmation` : ""}
          </p>
        ) : null}

        {busy && !data ? <Skeleton rows={6} /> : null}
        {isError ? (
          <Notice tone="amber" title="Couldn't load the payments" actionLabel="Try again" onAction={refetch}>
            Your records are fine; we just couldn't fetch them. Try again in a moment.
          </Notice>
        ) : null}

        {!isError && data ? (
          result.rows.length === 0 ? (
            <Notice title={active.length ? "No payments match these filters" : "No payments yet"} actionLabel={active.length ? "Clear filters" : "Record a payment"} onAction={active.length ? clearAll : undefined} actionTo={active.length ? undefined : "/super_admin/contribution/record_contribution"}>
              {active.length ? "Try removing a filter or widening the dates." : "Payments you record, and online payments members make, will appear here."}
            </Notice>
          ) : (
            <div style={{ opacity: isFetching ? 0.6 : 1 }}>
              <PaymentsTable rows={result.rows} />
              <Pager page={result.pagination.page} pages={result.pagination.pages} total={result.pagination.total} perPage={result.pagination.perPage} noun="payments" onPage={setPage} />
            </div>
          )
        ) : null}
      </div>
    </section>
  );
}
